import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import type { ExternalIdentifier, Label, Pro } from 'types/writer'

import { useGetRightsHolderByIdQuery } from '@disco/state-management/api/rights-holders'
import { updateSplit } from '@disco/state-management/reducers/rightsManagement'
import { getLabels } from '@disco/state-management/selectors/contacts'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { BaseDialog } from '../../components/base-dialog'
import { useRightsNewModelEnabled, useUpdateRightsEntity } from '../../hooks'
import type { RightsDataModalLocationState } from '../../types'
import { findDuplicateName } from '../../utils/validation'

export function EditLabelModal() {
  const dispatch = useDispatch()
  const location = useLocation()
  const { labelId } = useParams<{ labelId: string }>()
  const { splitId, splitsKey } =
    (location.state as RightsDataModalLocationState) || {}

  const rightsNewModelEnabled = useRightsNewModelEnabled()

  // New API: fetch by ID from route param
  const { data: rightsHolderData } = useGetRightsHolderByIdQuery(
    labelId ? parseInt(labelId, 10) : 0,
    { skip: !rightsNewModelEnabled || !labelId },
  )

  // Old API: lookup from Redux
  const labels = useSelector(getLabels)
  const labelsFromRedux = labels.find((l: Label) => l.id.toString() === labelId)

  // Use new API data or fall back to old Redux data
  const label: Label | undefined = useMemo(() => {
    if (rightsNewModelEnabled) {
      return rightsHolderData
        ? {
            id: rightsHolderData.id,
            name: rightsHolderData.title,
            external_identifiers: rightsHolderData.external_identifiers,
          }
        : undefined
    }
    return labelsFromRedux
  }, [rightsNewModelEnabled, rightsHolderData, labelsFromRedux])

  const [inputValue, setInputValue] = useState(label?.name || '')

  // Sync inputValue when pro changes (e.g., async load)
  useEffect(() => {
    if (label?.name) {
      setInputValue(label.name)
    }
  }, [label?.name])

  const duplicateLabelName = useMemo(
    () => (label ? findDuplicateName(inputValue, labels, label.id) : null),
    [inputValue, labels, label],
  )

  const warningMessage = duplicateLabelName
    ? `${duplicateLabelName} already exists. Please double-check if this is a duplicate before saving.`
    : null

  const handleNameChange = useCallback((value: string) => {
    setInputValue(value)
  }, [])

  const updateEntity = useUpdateRightsEntity({
    entityType: 'Label',
  })

  const handleSave = useCallback(
    async (
      name: string,
      externalIdentifiers: ExternalIdentifier[] | undefined,
    ) => {
      if (!label) return

      await updateEntity(
        label.id,
        name,
        externalIdentifiers,
        label.external_identifiers,
      )

      if (splitId && splitsKey) {
        dispatch(
          updateSplit({
            trackId: splitsKey,
            splitId,
            changes: { labelId: label.id },
          }),
        )
      }
    },
    [label, updateEntity, dispatch, splitId, splitsKey],
  )

  if (!label) {
    return null
  }

  return (
    <BaseDialog
      heading="Edit Label"
      nameFieldLabel="Label name"
      initialName={label.name}
      initialIdentifiers={label.external_identifiers}
      helperText="Editing this label will update it everywhere it's used."
      onSave={handleSave}
      isEditMode={true}
      warningMessage={warningMessage}
      onNameChange={handleNameChange}
      showISNI
      open
    />
  )
}

export default EditLabelModal
